import React from "react";

/* ---------------- small helpers ---------------- */

/**
 * Walk a JSON string and escape any bare control characters (U+0000–U+001F)
 * that appear inside string values without being escaped. This is the most
 * common reason GAM response bodies fail JSON.parse — the ad HTML contains
 * literal newlines / tabs embedded inside JS code that is itself a JSON string.
 */
function sanitizeJsonBody(str) {
    let out = '';
    let inStr = false;
    let i = 0;
    while (i < str.length) {
        const ch = str[i];
        if (inStr) {
            if (ch === '\\' && i + 1 < str.length) {
                // Already-escaped sequence — pass through as-is
                out += ch + str[i + 1];
                i += 2;
                continue;
            }
            if (ch === '"') { inStr = false; out += ch; i++; continue; }
            // Bare control character inside a JSON string — must escape it
            const code = ch.charCodeAt(0);
            if (code < 0x20) {
                if      (ch === '\n') out += '\\n';
                else if (ch === '\r') out += '\\r';
                else if (ch === '\t') out += '\\t';
                else out += `\\u${code.toString(16).padStart(4, '0')}`;
                i++; continue;
            }
        } else {
            if (ch === '"') inStr = true;
        }
        out += ch;
        i++;
    }
    return out;
}

const safeJson = (txt) => {
    if (txt == null || txt === "") return { ok: false, value: undefined, err: "empty" };
    if (typeof txt === "object") return { ok: true, value: txt, err: null };
    if (typeof txt === "string" && txt.startsWith("__BINARY__"))
        return { ok: false, value: undefined, err: "binary" };
    // First attempt: strict parse
    try { return { ok: true, value: JSON.parse(txt), err: null }; }
    catch (_) {}
    // Second attempt: sanitize bare control chars inside strings then re-parse
    // This handles ad_html containing raw newlines inside embedded JavaScript.
    try { return { ok: true, value: JSON.parse(sanitizeJsonBody(txt)), err: null }; }
    catch (e) { return { ok: false, value: undefined, err: e.message }; }
};

/* ---------------- GAM mobile info panel (replaces empty creative list) --- */
function parseCustParams(url = '') {
    try {
        const cp = new URL(url).searchParams.get('cust_params') || '';
        const decoded = (() => { try { return decodeURIComponent(decodeURIComponent(cp)); } catch { try { return decodeURIComponent(cp); } catch { return cp; } } })();
        const out = {};
        decoded.split('&').forEach(p => {
            const eq = p.indexOf('=');
            if (eq < 0) return;
            const k = p.slice(0, eq);
            const v = p.slice(eq + 1);
            try { out[decodeURIComponent(k)] = decodeURIComponent(v); } catch { out[k] = v; }
        });
        return out;
    } catch { return {}; }
}

function GAMInfoPanel({ req }) {
    const vd = req?.validData || {};
    const exp = vd.expanded || {};

    // URL-level fields
    let qs = {};
    try { new URL(req?.url || '').searchParams.forEach((v, k) => { qs[k] = v; }); } catch {}
    const cust = parseCustParams(req?.url || '');

    const adUnit   = vd.adUnit || qs.iu || '—';
    const sz       = (qs.sz || '').split('|').filter(Boolean).join(' · ') || '—';
    const correlator = vd.correlator || qs.c || qs.correlator || '—';

    // Demand / winning bid from cust_params
    const hbBidder = exp.hbBidder || cust.hb_bidder || '—';
    const hbPb     = exp.hbPb     || cust.hb_pb     || null;
    const hbDeal   = cust.hb_deal;
    const amznB    = cust.amzn_b  || cust.amznslots;
    const isAdX    = hbBidder === '—' && !amznB;
    const demand   = isAdX
        ? 'AdX / Open Auction'
        : amznB ? `Amazon APS${hbBidder !== '—' ? ` + Prebid (${hbBidder})` : ''}`
        : `Prebid — ${hbBidder}`;

    // Page / refresh context
    const isRefresh = !!(qs.prev_iu_szs || qs.prev_scp);
    const refreshCount = cust.refresh_count;

    // Body availability hint
    const body = req?.responseBody;
    const isBinary = typeof body === 'string' && body.startsWith('__BINARY__');
    const bodyNote = !body || body === ''
        ? 'Response body not in session (stripped from disk history on reconnect)'
        : isBinary
        ? 'Binary protobuf — GMA SDK responses cannot be decoded in the browser'
        : 'Response JSON present but no ad_networks structure found';

    const Row = ({ label, val, mono, warn, dim }) => {
        if (!val && val !== 0) return null;
        return (
            <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--line)' }}>
                <span style={{ width: 150, flexShrink: 0, fontSize: 11, color: 'var(--muted)' }}>{label}</span>
                <span style={{
                    fontSize: 11, flex: 1, wordBreak: 'break-all',
                    fontFamily: mono ? 'var(--font-mono)' : undefined,
                    color: warn ? 'var(--warn-text, #b45309)' : dim ? 'var(--muted)' : 'var(--ink)',
                }}>{val}</span>
            </div>
        );
    };

    return (
        <div style={{ padding: '12px 14px', overflowY: 'auto' }}>
            {/* Response note */}
            <div style={{
                marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 11,
                background: 'var(--warn-bg, #fffbeb)', border: '1px solid var(--warn-border, var(--line))', color: 'var(--warn-text, #92400e)',
            }}>
                ⚠ {bodyNote}
            </div>

            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                GAM Request
            </div>
            <Row label="Ad Unit" val={adUnit} mono />
            <Row label="Sizes" val={sz} />
            <Row label="Correlator" val={correlator} mono dim />
            <Row label="Request Type" val={isRefresh ? `Refresh${refreshCount != null ? ` #${refreshCount}` : ''}` : 'Initial load'} />

            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 6 }}>
                Demand
            </div>
            <Row label="Source" val={demand} warn={!isAdX && hbBidder !== '—'} />
            {hbPb && <Row label="hb_pb (price bucket)" val={`$${hbPb}`} mono />}
            {hbDeal && <Row label="Deal ID" val={hbDeal} mono />}
            {amznB && <Row label="APS token" val={String(amznB).slice(0, 30) + (String(amznB).length > 30 ? '…' : '')} mono dim />}

            {/* All hb_ and amzn_ cust_params */}
            {Object.entries(cust).filter(([k]) => /^(hb_|amzn)/i.test(k)).length > 0 && (
                <>
                    <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 6 }}>
                        Bid Params (cust_params)
                    </div>
                    {Object.entries(cust)
                        .filter(([k]) => /^(hb_|amzn)/i.test(k))
                        .map(([k, v]) => <Row key={k} label={k} val={v} mono />)
                    }
                </>
            )}

            {/* Mobian / contextual signals */}
            {Object.entries(cust).filter(([k]) => /^mobian_/i.test(k)).length > 0 && (
                <>
                    <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 6 }}>
                        Contextual Signals
                    </div>
                    {Object.entries(cust)
                        .filter(([k]) => /^mobian_/i.test(k))
                        .map(([k, v]) => <Row key={k} label={k} val={v} dim />)
                    }
                </>
            )}

            {/* Raw request URL — all query params */}
            <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0', userSelect: 'none' }}>
                    ▶ All Request Params ({Object.keys(qs).length})
                </summary>
                <div style={{ marginTop: 6, maxHeight: 240, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 4, padding: 6 }}>
                    {Object.entries(qs).map(([k, v]) => (
                        <div key={k} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', padding: '2px 0', borderBottom: '1px solid var(--line)', fontSize: 11 }}>
                            <span style={{ color: 'var(--muted)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k}</span>
                            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)', wordBreak: 'break-all', fontSize: 10 }}>{String(v).slice(0, 200)}{String(v).length > 200 ? '…' : ''}</span>
                        </div>
                    ))}
                </div>
            </details>

            {/* Response body */}
            <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0', userSelect: 'none' }}>
                    ▶ Response Body
                </summary>
                <div style={{ marginTop: 6 }}>
                    {(() => {
                        const body = req?.responseBody;
                        if (!body || body === '') {
                            return <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8 }}>Not captured — proxy strips response bodies on restart. Reconnect with the proxy running to capture fresh.</div>;
                        }
                        if (typeof body === 'string' && body.startsWith('__BINARY__')) {
                            return <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8 }}>Binary protobuf (GMA SDK format). Cannot be decoded in browser. Size: {((body.length - 10) / 1024).toFixed(1)} KB approx.</div>;
                        }
                        let pretty = body;
                        try { pretty = JSON.stringify(JSON.parse(body), null, 2); } catch {}
                        return (
                            <pre style={{ fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4, padding: 8, maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--ink)' }}>
                                {String(pretty).slice(0, 5000)}{String(pretty).length > 5000 ? '\n… (truncated)' : ''}
                            </pre>
                        );
                    })()}
                </div>
            </details>
        </div>
    );
}

const decodePlus = (s = "") => decodeURIComponent(String(s).replace(/\+/g, "%20"));

function qsToObj(qs) {
    const out = {};
    if (!qs) return out;
    const s = qs.replace(/^\?/, "");
    for (const part of s.split("&")) {
        if (!part) continue;
        const [k, v = ""] = part.split("=");
        if (!k) continue;
        const val = decodePlus(v);
        if (out[k] != null) Array.isArray(out[k]) ? out[k].push(val) : (out[k] = [out[k], val]);
        else out[k] = val;
    }
    return out;
}

/** keep scripts but neutralize so they won't execute */
function sanitizeScripts(html) {
    return String(html || "").replace(
        /<script(\b[^>]*)>([\s\S]*?)<\/script>/gi,
        (_m, attrs, code) => `<script type="text/plain"${attrs || ""}>${code}</script>`
    );
}

function buildDoc({ baseUrl, adHtml, live }) {
    const body = live ? (adHtml || "") : sanitizeScripts(adHtml || "");
    return [
        "<!doctype html>",
        "<meta charset='utf-8'>",
        baseUrl ? `<base href="${baseUrl}">` : "",
        "<style>html,body{margin:0;padding:0;background:transparent}</style>", // allow parent bg to show through
        body || "<div style='padding:12px;color:#90a4ae'>No ad_html found</div>",
    ].join("\n");
}

/* ---------------- HTML-lite parsing (no execution) ---------------- */

function parseHtmlLite(html) {
    const doc = document.implementation.createHTMLDocument("");
    // Scripts won't execute when inserted via innerHTML in a detached document.
    doc.body.innerHTML = String(html || "");
    return doc;
}

const uniq = (arr) => Array.from(new Set(arr || []));
const cut = (s, n = 120) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s) || "";
const hostOf = (url) => {
    try { return new URL(url, "https://x.invalid").host || ""; }
    catch { return ""; }
};

/** Parse params from either ?query or #fragment */
function getAllParamsFromUrl(url = "") {
    try {
        const u = new URL(url, "https://x.invalid");
        const all = [];
        if (u.search) all.push(u.search.slice(1));
        if (u.hash && u.hash.startsWith("#")) all.push(u.hash.slice(1));
        return all.reduce((acc, part) => Object.assign(acc, qsToObj(part)), {});
    } catch {
        const [, q = "", h = ""] = String(url).match(/^[^?#]*(?:\?([^#]*))?(?:#(.*))?/i) || [];
        return { ...qsToObj(q), ...qsToObj(h) };
    }
}

/** generic deep search: first property whose key matches (case-insensitive) */
function findValueByKey(obj, key) {
    if (!obj || typeof obj !== "object") return undefined;
    const want = String(key).toLowerCase();
    const stack = [obj];
    while (stack.length) {
        const cur = stack.pop();
        if (cur && typeof cur === "object") {
            for (const k of Object.keys(cur)) {
                if (k.toLowerCase() === want) return cur[k];
                const v = cur[k];
                if (v && typeof v === "object") stack.push(v);
            }
        }
    }
    return undefined;
}
const firstNonEmpty = (...vals) =>
    vals.find((v) => (Array.isArray(v) ? v.length : v != null && String(v).trim() !== "")) ?? undefined;

/* ---------------- wrapper sniffers ---------------- */

function detectMobian(html) {
    try {
        const doc = parseHtmlLite(html);
        const node = Array.from(doc.querySelectorAll("script")).find(
            (s) => String(s.id || "").toLowerCase() === "mobiantag"
        );
        if (!node) return null;

        const src = node.getAttribute("src") || "";
        const keysRaw = node.getAttribute("data-input-keys") || "";
        let keys = {};
        try { keys = JSON.parse(keysRaw); } catch { }
        const ids = {
            eaid: keys.eaid, ecid: keys.ecid, epid: keys.epid, eadv: keys.eadv, ebuy: keys.ebuy, impid: keys.impid,
        };
        return { present: true, src, ids, keys };
    } catch { return null; }
}

// in src/components/AdDebugModal.jsx
function detectDV(html) {
    try {
        const doc = parseHtmlLite(html);
        const scripts = Array.from(
            doc.querySelectorAll('script[src*="doubleverify.com"],script[src*="dvtp_src.js"]')
        );
        if (!scripts.length) return null;

        const first = scripts[0];
        const src = first.getAttribute("src") || "";
        const p = getAllParamsFromUrl(src);

        const details = {
            cmp: p.cmp,
            ctx: p.ctx,
            sadv: p.sadv,
            adsrv: p.adsrv,
            litm: p.litm,
            btadsrv: p.btadsrv,
            btreg: p.btreg,
            unit: p.unit,
            splc: p.splc,
            adu: p.adu,
            gdpr: p.gdpr,
            gdpr_consent: p.gdpr_consent,
        };

        return { present: true, src, details };
    } catch {
        return null;
    }
}

function detectOmidActiveView(html) {
    try {
        const doc = parseHtmlLite(html);
        const omidScripts = Array.from(
            doc.querySelectorAll('script[src*="/omsdk/"],script[src*="omid_session_bin.js"],script[src*="omsdk-v1.js"]')
        ).map((s) => s.getAttribute("src") || "");

        const avScripts = Array.from(
            doc.querySelectorAll('script[src*="/activeview/js/"],script#googleOmidDisplayScript')
        ).map((s) => s.getAttribute("src") || "");

        const avEl = doc.querySelector(".GoogleActiveViewElement");
        const avData = {};
        if (avEl) {
            for (const a of Array.from(avEl.attributes)) {
                if (a.name && a.name.startsWith("data-google-av-")) {
                    avData[a.name.replace("data-google-av-", "")] = a.value;
                }
            }
        }

        const present = !!(omidScripts.length || avScripts.length || avEl);
        if (!present) return null;
        return {
            present: true,
            omidScripts: uniq(omidScripts),
            activeViewScripts: uniq(avScripts),
            activeView: avEl ? avData : null,
        };
    } catch { return null; }
}

function detectPrebidUC(html) {
    try {
        const doc = parseHtmlLite(html);
        const s = Array.from(
            doc.querySelectorAll('script[src*="prebid-universal-creative"],script[src*="/creative.js"]')
        ).find((x) => /prebid[-_]universal[-_]creative|\/creative\.js/i.test(x.getAttribute("src") || ""));
        if (!s) return null;
        const src = s.getAttribute("src") || "";
        const m = src.match(/universal-creative@([\d.]+)/i);
        return { present: true, src, version: m ? m[1] : undefined };
    } catch { return null; }
}

function detectPixels(html) {
    try {
        const doc = parseHtmlLite(html);
        const imgs = Array.from(doc.querySelectorAll("img[src]")).map((i) => i.getAttribute("src"));
        const ifr = Array.from(doc.querySelectorAll("iframe[src]")).map((i) => i.getAttribute("src"));
        const all = uniq([...imgs, ...ifr].map((u) => (u || "").trim()).filter((u) => u && u !== "about:blank"));
        const domains = uniq(all.map((u) => hostOf(u)).filter(Boolean));
        return { count: all.length, urls: all, domains };
    } catch { return { count: 0, urls: [], domains: [] }; }
}

/* ---------------- extract ad_network items ---------------- */

/**
 * Pulls per-network rows and enriches with:
 *  - request_type (response_type/ad_type/req.type fallback)
 *  - slot_sizes (array)
 *  - pubid (raw path) and publisher_id (ca-pub-#######)
 */
function extractItems(gamJson, reqTypeFallback) {
    const nets = Array.isArray(gamJson?.ad_networks) ? gamJson.ad_networks : [];
    return nets.map((n, i) => {
        const ad = n?.ad || {};
        const adHtml = ad.ad_html || ad.html || "";
        const baseUrl = ad.ad_base_url || "";

        // debug_dialog can be at the network level or at the root
        const dds = n?.debug_dialog || gamJson?.debug_dialog || "";
        const dd = qsToObj(dds);

        const extraRaw = gamJson?.settings?.extra_response_info;
        const extra = typeof extraRaw === "string" ? safeJson(extraRaw).value : extraRaw || {};

        // Request type + slot sizes
        const request_type =
            firstNonEmpty(
                n?.response_type,
                ad?.response_type,
                findValueByKey(n, "response_type"),
                n?.ad_type,
                findValueByKey(n, "ad_type"),
                reqTypeFallback
            ) || "";

        const slot_sizes_raw =
            firstNonEmpty(
                n?.slot_sizes,
                ad?.slot_sizes,
                findValueByKey(n, "slot_sizes"),
                findValueByKey(ad, "slot_sizes")
            ) || [];

        const slot_sizes = Array.isArray(slot_sizes_raw)
            ? slot_sizes_raw
            : (typeof slot_sizes_raw === "string" && slot_sizes_raw !== "")
                ? [slot_sizes_raw]
                : [];

        // pubid (raw) and extracted ca-pub-#### id
        const pubid_path = firstNonEmpty(n?.data?.pubid, findValueByKey(n, "pubid")) || "";
        const publisher_id = (() => {
            const m = String(pubid_path).match(/ca-pub-\d+/i);
            return m ? m[0] : "";
        })();

        const rule_line_external_id =
            firstNonEmpty(n?.rule_line_external_id, findValueByKey(n, "rule_line_external_id")) || "";

        const meta = {
            ad_type: n?.ad_type || "",
            ad_size: n?.ad_size || n?.size || "",
            ad_base_url: baseUrl || "",

            request_type,
            slot_sizes,
            rule_line_external_id,

            // NEW:
            pubid_path,
            publisher_id,

            // From debug_dialog / extra_response_info
            app_id: dd.AppID || dd.appid || "",
            creative_id_debug: dd.Creatives || dd.creatives || "",
            line_item_debug: dd.Lineitems || dd.lineitems || "",
            creative_id_extra: extra?.creative_id || "",
            line_item_extra: extra?.line_item_id || "",

            // Query IDs
            backend_qid:
                n?.debug_signals?.backend_query_id || gamJson?.debug_signals?.backend_query_id || "",
            gws_query_id: n?.gws_query_id || gamJson?.gws_query_id || "",
            allocation_id: n?.allocation_id || "",
            ad_source: n?.ad_source_instance_name || "",
        };

        // wrapper sniffing
        const wrappers = {
            mobian: detectMobian(adHtml),
            doubleverify: detectDV(adHtml),
            omidActiveView: detectOmidActiveView(adHtml),
            prebidUC: detectPrebidUC(adHtml),
            pixels: detectPixels(adHtml),
        };

        const label = `ad_networks[${i}] • ${meta.request_type || meta.ad_type || "unknown"}${meta.ad_size
            ? ` • ${meta.ad_size}`
            : meta.slot_sizes?.length
                ? ` • ${meta.slot_sizes.join(", ")}`
                : ""
            }`.trim();

        return { label, adHtml, baseUrl, meta, wrappers };
    });
}

/* ---- download helper ---- */
function downloadHtml(html, filename = 'creative.html') {
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
}

/* ---------------- modal ---------------- */

export default function AdDebugModal({ open, onClose, req }) {
    const [index, setIndex] = React.useState(0);
    const [live, setLive] = React.useState(false);
    const [rendered, setRendered] = React.useState(false);

    // Prebid cache fetch state
    const [cacheFetchState, setCacheFetchState] = React.useState('idle'); // idle | fetching | done | error
    const [cacheCreativeItem, setCacheCreativeItem] = React.useState(null);
    const [cacheFetchError, setCacheFetchError] = React.useState('');

    React.useEffect(() => { setRendered(false); }, [open, index]);
    React.useEffect(() => {
        if (!open) {
            setIndex(0); setLive(false); setRendered(false);
            setCacheFetchState('idle'); setCacheCreativeItem(null); setCacheFetchError('');
        }
    }, [open]);

    // parse ONLY when open (on-demand)
    const parsed = React.useMemo(
        () => (open ? safeJson(req?.responseBody) : { ok: false }),
        [open, req?.responseBody]
    );

    // pass a fallback (row-level) request type into extraction
    const reqTypeFallback = React.useMemo(
        () => req?.requestType || req?.type || "",
        [req?.requestType, req?.type]
    );

    const items = React.useMemo(
        () => (parsed.ok ? extractItems(parsed.value, reqTypeFallback) : []),
        [parsed, reqTypeFallback]
    );

    // Extract Prebid cache params from cust_params
    const cacheParams = React.useMemo(() => {
        if (!req?.url) return null;
        const cust = parseCustParams(req.url);
        const cacheId   = cust.hb_cache_id;
        const cacheHost = cust.hb_cache_host;
        const cachePath = cust.hb_cache_path || '/prebid/cache';
        if (!cacheId || !cacheHost) return null;
        return {
            cacheId,
            cacheHost,
            cachePath,
            bidder: cust.hb_bidder || '?',
            size:   cust.hb_size   || '',
            url:    `https://${cacheHost}${cachePath}?uuid=${cacheId}`,
        };
    }, [req?.url]);

    // Fetch creative HTML from Prebid cache server — routed through our proxy
    // to avoid CORS restrictions. Direct fetch from the browser fails because
    // adnxs.com / Prebid cache servers don't set Access-Control-Allow-Origin.
    const fetchFromCache = React.useCallback(async () => {
        if (!cacheParams) return;
        setCacheFetchState('fetching');
        setCacheCreativeItem(null);
        setCacheFetchError('');
        try {
            // Use our server-side proxy endpoint to bypass CORS
            const PROXY_API = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');
            const proxyUrl = `${PROXY_API}/proxy/fetch-url?url=${encodeURIComponent(cacheParams.url)}`;
            const resp = await fetch(proxyUrl);
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({ error: resp.statusText }));
                throw new Error(err.error || `HTTP ${resp.status}`);
            }
            const html = await resp.text();
            const wrappers = {
                mobian:        detectMobian(html),
                doubleverify:  detectDV(html),
                omidActiveView:detectOmidActiveView(html),
                prebidUC:      detectPrebidUC(html),
                pixels:        detectPixels(html),
            };
            setCacheCreativeItem({
                label:   `🏆 Prebid Cache — ${cacheParams.bidder}${cacheParams.size ? ` • ${cacheParams.size}` : ''}`,
                adHtml:  html,
                baseUrl: `https://${cacheParams.cacheHost}`,
                meta:    {
                    request_type:          'prebid_cache',
                    ad_size:               cacheParams.size,
                    rule_line_external_id: '',
                    pubid_path:            '',
                    publisher_id:          '',
                    app_id:                '',
                    creative_id_debug:     '',
                    line_item_debug:       '',
                    creative_id_extra:     '',
                    line_item_extra:       '',
                    backend_qid:           '',
                    gws_query_id:          '',
                    allocation_id:         '',
                    ad_source:             cacheParams.bidder,
                },
                wrappers,
            });
            setCacheFetchState('done');
        } catch (e) {
            setCacheFetchError(e.message);
            setCacheFetchState('error');
        }
    }, [cacheParams]);

    // Extract ad_html from the response body.
    // Strategy 1: standard JSON.parse via `parsed` (works for most responses).
    // Strategy 2: character-walk the raw string — handles bodies where JSON.parse
    //   fails due to unescaped control chars / line-breaks inside JS code in ad_html.
    const capturedAdHtml = React.useMemo(() => {
        // Strategy 1 — JSON.parse succeeded
        if (parsed.ok && parsed.value) {
            const nets = parsed.value?.ad_networks;
            if (Array.isArray(nets)) {
                for (const n of nets) {
                    const h = n?.ad?.ad_html || n?.ad?.html || n?.ad_html || n?.html;
                    if (h && typeof h === 'string' && h.trim()) return h;
                }
            }
            const found = findValueByKey(parsed.value, 'ad_html') || findValueByKey(parsed.value, 'ad_markup');
            if (found) return found;
        }

        // Strategy 2 — JSON.parse failed (e.g. unescaped newlines in ad JS code).
        // Walk the raw body string to extract the first "ad_html":"..." value.
        const rawBody = req?.responseBody;
        if (!rawBody || typeof rawBody !== 'string' || rawBody.startsWith('__BINARY__')) return null;

        try {
            const KEY = '"ad_html":';
            let ki = rawBody.indexOf(KEY);
            if (ki === -1) return null;
            let i = ki + KEY.length;
            while (i < rawBody.length && rawBody[i] === ' ') i++;
            if (rawBody[i] !== '"') return null;
            i++; // skip opening quote

            let result = '';
            while (i < rawBody.length) {
                const ch = rawBody[i];
                if (ch === '\\' && i + 1 < rawBody.length) {
                    const nx = rawBody[i + 1];
                    if      (nx === '"')  { result += '"';  i += 2; }
                    else if (nx === '\\') { result += '\\'; i += 2; }
                    else if (nx === 'n')  { result += '\n'; i += 2; }
                    else if (nx === 'r')  { result += '\r'; i += 2; }
                    else if (nx === 't')  { result += '\t'; i += 2; }
                    else if (nx === 'u' && i + 5 < rawBody.length) {
                        const hex = rawBody.slice(i + 2, i + 6);
                        if (/^[\da-fA-F]{4}$/.test(hex)) {
                            result += String.fromCharCode(parseInt(hex, 16));
                            i += 6;
                        } else { result += nx; i += 2; }
                    } else { result += nx; i += 2; }
                } else if (ch === '"') {
                    break; // end of JSON string
                } else {
                    result += ch;
                    i++;
                }
            }
            return result.trim() ? result : null;
        } catch { return null; }
    }, [parsed, req?.responseBody]);

    // Synthetic item from captured body (used when extractItems returns empty but we found ad_html)
    const capturedItem = React.useMemo(() => {
        if (items.length > 0 || !capturedAdHtml) return null;
        const baseUrl = (() => { try { const u = new URL(req?.url || ''); return `${u.protocol}//${u.host}`; } catch { return ''; } })();
        const wrappers = {
            mobian:         detectMobian(capturedAdHtml),
            doubleverify:   detectDV(capturedAdHtml),
            omidActiveView: detectOmidActiveView(capturedAdHtml),
            prebidUC:       detectPrebidUC(capturedAdHtml),
            pixels:         detectPixels(capturedAdHtml),
        };
        return {
            label: '📡 Captured creative (from intercepted response)',
            adHtml: capturedAdHtml,
            baseUrl,
            meta: {
                request_type: 'captured', ad_size: '', slot_sizes: [],
                rule_line_external_id: '', pubid_path: '', publisher_id: '',
                app_id: '', creative_id_debug: '', line_item_debug: '',
                creative_id_extra: '', line_item_extra: '',
                backend_qid: '', gws_query_id: '', allocation_id: '',
                ad_source: 'captured response',
            },
            wrappers,
        };
    }, [items.length, capturedAdHtml, req?.url]);

    // All items: real parsed items → captured fallback → Prebid cache fetch
    const allItems = React.useMemo(() => {
        if (items.length > 0) return items;
        if (capturedItem) return [capturedItem];
        if (cacheCreativeItem) return [cacheCreativeItem];
        return [];
    }, [items, capturedItem, cacheCreativeItem]);

    const selected = allItems[index] || null;

    const doc = React.useMemo(() => {
        if (!rendered || !selected) return "";
        return buildDoc({ baseUrl: selected.baseUrl, adHtml: selected.adHtml, live });
    }, [rendered, selected, live]);

    // Reason why no creative is available (only used when allItems is still empty)
    const noCreativeReason = React.useMemo(() => {
        const body = req?.responseBody;
        if (!body || body === '') return 'response_missing';
        if (typeof body === 'string' && body.startsWith('__BINARY__')) return 'binary_protobuf';
        // If JSON parsed but no ad_html found anywhere → no fill
        if (parsed.ok && !capturedAdHtml) return 'no_ad_html';
        // Body exists but both JSON.parse and character-walk found nothing → no ad markup
        if (!parsed.ok && !capturedAdHtml) return 'no_ad_html';
        return 'parse_error';
    }, [req?.responseBody, parsed.ok, capturedAdHtml]);

    if (!open) return null;

    const w = selected?.wrappers || {};
    const omid = w.omidActiveView;
    const dv = w.doubleverify;
    const mobian = w.mobian;
    const prebidUC = w.prebidUC;
    const pixels = w.pixels || { count: 0, urls: [], domains: [] };

    // Whether we can show render controls (have HTML to render)
    const canRender = allItems.length > 0;
    // True when creative came from Prebid cache (no response body)
    const isCacheMode = items.length === 0;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <strong>Creative Debugger</strong>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {canRender && (
                            <>
                                <label style={styles.chk} title="Safe mode prevents auto execution">
                                    <input
                                        type="checkbox"
                                        checked={live}
                                        onChange={(e) => setLive(e.target.checked)}
                                        disabled={!rendered}
                                    />
                                    <span>Live (run scripts)</span>
                                </label>
                                <button
                                    style={styles.btnPrimary}
                                    onClick={() => setRendered(true)}
                                    disabled={!selected || rendered}
                                >
                                    Render
                                </button>
                                <button style={styles.btn} onClick={() => setRendered(false)} disabled={!rendered}>
                                    Reset
                                </button>
                                {selected?.adHtml && (
                                    <button
                                        style={styles.btn}
                                        title="Download creative HTML"
                                        onClick={() => {
                                            const slug = (selected.meta?.ad_source || selected.meta?.request_type || 'creative')
                                                .replace(/[^a-z0-9]/gi, '_').slice(0, 32);
                                            downloadHtml(selected.adHtml, `${slug}.html`);
                                        }}
                                    >
                                        ⬇ HTML
                                    </button>
                                )}
                            </>
                        )}
                        <button style={styles.btn} onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>

                {/* ── No captured creative — show reason + optional Prebid cache fetch ── */}
                {isCacheMode && allItems.length === 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', minHeight: 480 }}>

                        {/* LEFT: why no creative + Prebid cache fetch if available */}
                        <div style={{ borderRight: '1px solid var(--line)', overflowY: 'auto', padding: '16px 14px' }}>
                            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                                Creative Not Available
                            </div>

                            {/* Reason */}
                            <div style={{ fontSize: 12, color: 'var(--ink)', lineHeight: 1.6, marginBottom: 16 }}>
                                {noCreativeReason === 'binary_protobuf' && <>
                                    <strong>GMA SDK binary response.</strong> The iOS/Android GMA SDK receives a binary protobuf — the creative HTML is not exposed over the network.
                                    {cacheParams ? ' However, the Prebid cache URL is available below.' : ' No Prebid cache info found in this request.'}
                                </>}
                                {noCreativeReason === 'response_missing' && <>
                                    <strong>Response body not here.</strong> Either it was trimmed to save memory (older than the newest requests the dashboard keeps full bodies for), or the proxy never captured it (gzip/QUIC/HTTP-3, or a disk-history strip on restart). Re-capture this request live to see the creative.
                                </>}
                                {noCreativeReason === 'no_ad_html' && <>
                                    <strong>No ad markup in response.</strong> The GAM response is JSON but contains no ad HTML — this slot likely received no fill (house ad, backfill, or empty auction).
                                </>}
                                {noCreativeReason === 'parse_error' && <>
                                    <strong>Response could not be parsed.</strong> The body exists but is not valid JSON or a recognised format.
                                </>}
                            </div>

                            {/* Prebid cache fetch — only when hb_cache_host + hb_cache_id are present */}
                            {cacheParams && (
                                <>
                                    <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                                        Fetch from Prebid Cache
                                    </div>
                                    <code style={{ fontSize: 10, color: 'var(--ink)', wordBreak: 'break-all', display: 'block', marginBottom: 10 }}>{cacheParams.url}</code>
                                    {cacheFetchState === 'idle' && (
                                        <button style={styles.btnPrimary} onClick={fetchFromCache}>
                                            ⬇ Fetch Creative HTML
                                        </button>
                                    )}
                                    {cacheFetchState === 'fetching' && (
                                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>⏳ Fetching…</span>
                                    )}
                                    {cacheFetchState === 'error' && (
                                        <span style={{ fontSize: 11, color: 'var(--error-text, #dc2626)' }}>
                                            Failed: {cacheFetchError}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* RIGHT: GAM request metadata */}
                        <div style={{ overflowY: 'auto' }}>
                            <GAMInfoPanel req={req} />
                        </div>
                    </div>
                )}

                {/* ── When creatives exist (from response body OR Prebid cache): 2-column inspector ── */}
                {allItems.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 12, minHeight: 440 }}>
                    {/* LEFT */}
                    <div style={styles.left}>
                        <div style={styles.subHeader}>Creatives</div>
                        <div style={{ maxHeight: 180, overflow: "auto", borderBottom: "1px solid var(--line)" }}>
                            {allItems.map((it, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIndex(i)}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "8px 10px",
                                        border: "none",
                                        borderBottom: "1px solid var(--line)",
                                        background: i === index ? "var(--row-active)" : "var(--card)",
                                        color: i === index ? "var(--primary)" : "var(--ink)",
                                        cursor: "pointer",
                                        fontSize: 13,
                                    }}
                                    title={it.label}
                                >
                                    {it.label}
                                </button>
                            ))}
                        </div>

                        <div style={{ padding: 10 }}>
                            <div style={styles.subHeader}>Meta / Debug</div>
                            {!selected ? (
                                <div style={{ color: "var(--muted)" }}>(select a creative)</div>
                            ) : (
                                <table style={styles.metaTable}>
                                    <tbody>
                                        <MetaRow k="request_type" v={selected.meta.request_type} />
                                        <MetaRow
                                            k="slot_size(s)"
                                            v={
                                                selected.meta.slot_sizes?.length
                                                    ? selected.meta.slot_sizes.join(", ")
                                                    : selected.meta.ad_size || "—"
                                            }
                                        />
                                        <MetaRow k="rule_line_external_id" v={selected.meta.rule_line_external_id} />
                                        <MetaRow k="ad_base_url" v={selected.meta.ad_base_url} />

                                        <tr><td colSpan={2} style={styles.metaSep}>— Publisher —</td></tr>
                                        <MetaRow k="publisher_id" v={selected.meta.publisher_id} />
                                        <MetaRow k="pubid (raw)" v={selected.meta.pubid_path} />

                                        <tr><td colSpan={2} style={styles.metaSep}>— IDs from <code>debug_dialog</code> —</td></tr>
                                        <MetaRow k="AppID" v={selected.meta.app_id} />
                                        <MetaRow k="Creative (debug)" v={selected.meta.creative_id_debug} />
                                        <MetaRow k="Line item (debug)" v={selected.meta.line_item_debug} />

                                        <tr><td colSpan={2} style={styles.metaSep}>— IDs from <code>settings.extra_response_info</code> —</td></tr>
                                        <MetaRow k="Creative (extra)" v={selected.meta.creative_id_extra} />
                                        <MetaRow k="Line item (extra)" v={selected.meta.line_item_extra} />

                                        <tr><td colSpan={2} style={styles.metaSep}>— Query IDs —</td></tr>
                                        <MetaRow k="backend_query_id" v={selected.meta.backend_qid} />
                                        <MetaRow k="gws_query_id" v={selected.meta.gws_query_id} />
                                        <MetaRow k="allocation_id" v={selected.meta.allocation_id} />
                                        <MetaRow k="ad_source" v={selected.meta.ad_source} />

                                        <tr><td colSpan={2} style={styles.metaSep}>— Creative wrappers —</td></tr>

                                        {/* DV */}
                                        <MetaRow k="DoubleVerify" v={dv ? "present" : "—"} />
                                        {dv && (
                                            <>
                                                <MetaRow k="DV src" v={dv.src || "—"} />
                                                <MetaRow
                                                    k="DV params"
                                                    v={Object.entries(dv.details)
                                                        .filter(([, v]) => v != null && v !== "")
                                                        .map(([k, v]) => `${k}=${v}`)
                                                        .join("  • ")}
                                                />
                                            </>
                                        )}

                                        {/* Mobian */}
                                        <MetaRow k="Mobian wrapper" v={mobian ? "present" : "—"} />
                                        {mobian && (
                                            <>
                                                <MetaRow k="Mobian src" v={mobian.src || "—"} />
                                                <MetaRow
                                                    k="Mobian IDs"
                                                    v={[
                                                        mobian.ids?.eaid && `eaid=${mobian.ids.eaid}`,
                                                        mobian.ids?.ecid && `ecid=${mobian.ids.ecid}`,
                                                        mobian.ids?.epid && `epid=${mobian.ids.epid}`,
                                                        mobian.ids?.eadv && `eadv=${mobian.ids.eadv}`,
                                                        mobian.ids?.ebuy && `ebuy=${mobian.ids.ebuy}`,
                                                        mobian.ids?.impid && `impid=${mobian.ids.impid}`,
                                                    ]
                                                        .filter(Boolean)
                                                        .join("  • ")}
                                                />
                                            </>
                                        )}

                                        {/* OMID / ActiveView */}
                                        <MetaRow k="OMID / ActiveView" v={omid ? "present" : "—"} />
                                        {omid && (
                                            <>
                                                {omid.omidScripts?.length ? (
                                                    <MetaRow
                                                        k="OMID scripts"
                                                        v={omid.omidScripts.map((s) => cut(s, 80)).join("  • ")}
                                                    />
                                                ) : null}
                                                {omid.activeViewScripts?.length ? (
                                                    <MetaRow
                                                        k="ActiveView scripts"
                                                        v={omid.activeViewScripts.map((s) => cut(s, 80)).join("  • ")}
                                                    />
                                                ) : null}
                                                {omid.activeView ? (
                                                    <MetaRow
                                                        k="ActiveView data"
                                                        v={Object.entries(omid.activeView)
                                                            .slice(0, 8)
                                                            .map(([k, v]) => `${k}=${cut(String(v), 32)}`)
                                                            .join("  • ")}
                                                    />
                                                ) : null}
                                            </>
                                        )}

                                        {/* Prebid UC */}
                                        <MetaRow k="Prebid UC" v={prebidUC ? "present" : "—"} />
                                        {prebidUC && (
                                            <MetaRow
                                                k="Prebid details"
                                                v={[
                                                    prebidUC.version && `version=${prebidUC.version}`,
                                                    prebidUC.src && cut(prebidUC.src, 80),
                                                ]
                                                    .filter(Boolean)
                                                    .join("  • ")}
                                            />
                                        )}

                                        <tr><td colSpan={2} style={styles.metaSep}>— Pixels / Beacons —</td></tr>
                                        <MetaRow
                                            k="Domains"
                                            v={pixels.domains.length ? pixels.domains.join("  • ") : "—"}
                                        />
                                        <tr>
                                            <td style={styles.metaKey}>URLs</td>
                                            <td style={{ ...styles.metaVal, maxWidth: 0 }}>
                                                <div style={{ maxHeight: 120, overflow: "auto" }}>
                                                    {pixels.urls.length ? (
                                                        pixels.urls.map((u, i) => (
                                                            <div key={i} style={{ marginBottom: 4 }}>
                                                                {cut(u, 140)}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span>—</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* In cache mode: show GAM request details below the meta table */}
                        {isCacheMode && (
                            <details style={{ borderTop: '1px solid var(--line)', marginTop: 4 }}>
                                <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '6px 10px', userSelect: 'none' }}>
                                    ▶ GAM Request Details
                                </summary>
                                <GAMInfoPanel req={req} />
                            </details>
                        )}
                    </div>

                    {/* RIGHT */}
                    <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 8 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <strong>Preview</strong>
                            <span style={{ color: "var(--muted)", fontSize: 12 }}>
                                {rendered
                                    ? live
                                        ? "Scripts enabled in sandboxed iframe"
                                        : "Safe mode (scripts disabled)"
                                    : "Not rendered — click Render to load the creative"}
                            </span>
                            {rendered && selected && (
                                <>
                                    <button
                                        style={styles.btn}
                                        onClick={() => {
                                            const w = window.open("", "_blank");
                                            if (!w) return;
                                            w.document.open();
                                            w.document.write(doc);
                                            w.document.close();
                                        }}
                                    >
                                        Open
                                    </button>

                                    {/* Safe copy (scripts neutralized) */}
                                    <button
                                        style={styles.btn}
                                        title="Copies ad_html with scripts neutralized (type=text/plain)"
                                        onClick={async () => {
                                            try {
                                                const safeCopy = sanitizeScripts(selected.adHtml || "");
                                                await navigator.clipboard.writeText(safeCopy);
                                                alert("ad_html (safe) copied");
                                            } catch { }
                                        }}
                                    >
                                        Copy ad_html
                                    </button>

                                    {/* Raw copy */}
                                    <button
                                        style={styles.btn}
                                        title="Copies raw ad_html exactly as returned by GAM"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(selected.adHtml || "");
                                                alert("ad_html (raw) copied");
                                            } catch { }
                                        }}
                                    >
                                        Copy raw
                                    </button>
                                </>
                            )}
                        </div>

                        <div style={styles.previewBox}>
                            {!rendered ? (
                                <div style={{ padding: 16, color: "var(--muted)" }}>
                                    Creative is not rendered yet. Click <b>Render</b> to load it here.
                                </div>
                            ) : (
                                <iframe
                                    key={`${index}-${live}-${rendered}`}
                                    title="ad-preview"
                                    sandbox={
                                        live
                                            ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                                            : ""
                                    }
                                    srcDoc={doc}
                                    style={{ width: "100%", height: "100%", border: 0 }}
                                />
                            )}
                        </div>
                    </div>
                </div>}
            </div>
        </div>
    );
}

/* ---------------- presentational bits ---------------- */

function MetaRow({ k, v }) {
    return (
        <tr>
            <td style={styles.metaKey}>{k}</td>
            <td style={styles.metaVal}>{v || "—"}</td>
        </tr>
    );
}

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.35)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
    },
    modal: {
        width: "min(1180px, 98vw)",
        maxHeight: "92vh",
        overflow: "auto",
        background: "var(--card)",
        border: "1px solid var(--line)",
        borderRadius: 12,
        boxShadow: "var(--shadow-lg)",
        color: "var(--ink)"
    },
    header: {
        padding: "10px 12px",
        borderBottom: "1px solid var(--line)",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    left: { borderRight: "1px solid var(--line)", overflow: "hidden" },
    subHeader: {
        fontWeight: 600,
        padding: "8px 10px",
        background: "var(--bg)",
        borderBottom: "1px solid var(--line)",
        color: "var(--ink)"
    },
    previewBox: {
        border: "1px solid var(--line)",
        borderRadius: 10,
        overflow: "hidden",
        minHeight: 360,
        background: "var(--card)", // Consistent dark background
    },
    btn: {
        padding: "6px 10px",
        fontSize: 12,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--card)",
        color: "var(--ink)",
        cursor: "pointer",
    },
    btnPrimary: {
        padding: "6px 12px",
        fontSize: 12,
        border: "1px solid var(--primary)",
        borderRadius: 8,
        background: "var(--primary)",
        color: "#fff",
        cursor: "pointer",
    },
    chk: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)" },
    metaTable: { width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" },
    metaKey: {
        width: 170,
        padding: "4px 6px",
        color: "var(--muted)",
        fontWeight: 600,
        borderBottom: "1px solid var(--line)",
        verticalAlign: "top",
    },
    metaVal: { padding: "4px 6px", borderBottom: "1px solid var(--line)", wordBreak: "break-word", color: "var(--ink)" },
    metaSep: { padding: "8px 6px", color: "var(--muted)", borderBottom: "1px solid var(--line)" },
};
